/**
 * FishPreview - 3D rotating fish preview modal when catching a fish
 */

import * as THREE from 'three';
import { createCelMesh } from '../shaders/CelShader.js';

// Preview state
let previewScene = null;
let previewCamera = null;
let previewRenderer = null;
let previewFish = null;
let animationId = null;
let isPreviewOpen = false;

// Callbacks
let onCloseCallback = null;

/**
 * Initialize the fish preview system
 */
export function initFishPreview() {
  const canvas = document.getElementById('fish-preview-canvas');
  if (!canvas) {
    console.warn('Fish preview canvas not found');
    return;
  }

  // Create scene
  previewScene = new THREE.Scene();
  previewScene.background = null; // Transparent background

  // Create camera
  previewCamera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
  previewCamera.position.set(0, 0, 5);
  previewCamera.lookAt(0, 0, 0);

  // Create renderer
  previewRenderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true
  });
  previewRenderer.setSize(200, 200);
  previewRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  // Add lighting
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
  previewScene.add(ambientLight);

  const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
  directionalLight.position.set(2, 3, 4);
  previewScene.add(directionalLight);

  const backLight = new THREE.DirectionalLight(0xffffff, 0.3);
  backLight.position.set(-2, -1, -2);
  previewScene.add(backLight);

  // Set up close button
  const closeBtn = document.getElementById('fish-preview-close');
  if (closeBtn) {
    closeBtn.addEventListener('click', closeFishPreview);
  }

  // Set up continue button
  const continueBtn = document.getElementById('fish-preview-continue');
  if (continueBtn) {
    continueBtn.addEventListener('click', closeFishPreview);
  }

  // Click outside to close
  const modal = document.getElementById('fish-preview-modal');
  if (modal) {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeFishPreview();
      }
    });
  }

  // Escape to close
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isPreviewOpen) {
      closeFishPreview();
    }
  });
}

/**
 * Create a fish model for preview (clone of the original)
 */
function createPreviewFish(fishType) {
  const fishGroup = new THREE.Group();
  const size = fishType.size;
  const color = fishType.color;

  // Create fish body
  const bodyGeometry = new THREE.CapsuleGeometry(size * 0.5, size, 8, 8);
  const fishMesh = createCelMesh(bodyGeometry, color);
  fishGroup.add(fishMesh);

  // Add tail
  const tailGeometry = new THREE.ConeGeometry(size * 0.4, size * 0.8, 4);
  const tailMesh = createCelMesh(tailGeometry, color);
  tailMesh.position.set(-size * 0.8, 0, 0);
  tailMesh.rotation.z = Math.PI / 2;
  fishGroup.add(tailMesh);

  // Add fins
  const finGeometry = new THREE.ConeGeometry(size * 0.2, size * 0.4, 4);

  // Top fin
  const topFinMesh = createCelMesh(finGeometry, color);
  topFinMesh.position.set(0, size * 0.5, 0);
  topFinMesh.rotation.z = -Math.PI / 2;
  fishGroup.add(topFinMesh);

  // Side fins
  const leftFinMesh = createCelMesh(finGeometry, color);
  leftFinMesh.position.set(0, 0, size * 0.5);
  leftFinMesh.rotation.x = Math.PI / 2;
  leftFinMesh.rotation.z = -Math.PI / 2;
  leftFinMesh.scale.set(0.7, 0.7, 0.7);
  fishGroup.add(leftFinMesh);

  const rightFinMesh = createCelMesh(finGeometry, color);
  rightFinMesh.position.set(0, 0, -size * 0.5);
  rightFinMesh.rotation.x = -Math.PI / 2;
  rightFinMesh.rotation.z = -Math.PI / 2;
  rightFinMesh.scale.set(0.7, 0.7, 0.7);
  fishGroup.add(rightFinMesh);

  // Add eyes
  const eyeGeometry = new THREE.SphereGeometry(size * 0.1, 8, 8);
  const eyeMaterial = new THREE.MeshBasicMaterial({ color: 0xFFFFFF });

  const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
  leftEye.position.set(size * 0.4, size * 0.2, size * 0.3);
  fishGroup.add(leftEye);

  const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
  rightEye.position.set(size * 0.4, size * 0.2, -size * 0.3);
  fishGroup.add(rightEye);

  // Add pupils
  const pupilGeometry = new THREE.SphereGeometry(size * 0.05, 8, 8);
  const pupilMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });

  const leftPupil = new THREE.Mesh(pupilGeometry, pupilMaterial);
  leftPupil.position.set(size * 0.45, size * 0.22, size * 0.3);
  fishGroup.add(leftPupil);

  const rightPupil = new THREE.Mesh(pupilGeometry, pupilMaterial);
  rightPupil.position.set(size * 0.45, size * 0.22, -size * 0.3);
  fishGroup.add(rightPupil);

  return fishGroup;
}

/**
 * Show fish preview modal
 * @param {Object} fish - The fish data
 * @param {boolean} isNewDiscovery - Whether this is a first-time catch
 * @param {Function} callback - Called when preview is closed
 * @param {Object} bonusInfo - Optional bonus info { bonusPoints, streakCount }
 */
export function showFishPreview(fish, isNewDiscovery = false, callback = null, bonusInfo = null) {
  if (!previewScene || !previewRenderer) {
    initFishPreview();
  }

  onCloseCallback = callback;
  isPreviewOpen = true;

  // Remove old fish if exists
  if (previewFish) {
    previewScene.remove(previewFish);
    previewFish = null;
  }

  // Create new fish for preview
  previewFish = createPreviewFish(fish);

  // Scale to fit nicely in view
  const scale = 2.0 / fish.size;
  previewFish.scale.set(scale, scale, scale);

  previewScene.add(previewFish);

  // Update modal content
  const modal = document.getElementById('fish-preview-modal');
  const nameEl = document.getElementById('fish-preview-name');
  const rarityEl = document.getElementById('fish-preview-rarity');
  const pointsEl = document.getElementById('fish-preview-points');
  const newBadge = document.getElementById('fish-preview-new');
  const titleEl = document.getElementById('fish-preview-title');

  if (nameEl) nameEl.textContent = fish.name;
  if (rarityEl) {
    rarityEl.textContent = fish.rarity.charAt(0).toUpperCase() + fish.rarity.slice(1);
    rarityEl.className = `fish-preview-rarity ${fish.rarity}`;
  }

  // Display points with streak bonus if applicable
  if (pointsEl) {
    if (bonusInfo && bonusInfo.bonusPoints > 0) {
      pointsEl.innerHTML = `+${fish.points} <span style="color: #FF9500; font-size: 14px;">(+${bonusInfo.bonusPoints} streak bonus!)</span>`;
    } else {
      pointsEl.textContent = `+${fish.points} points`;
    }
  }

  if (newBadge) newBadge.style.display = isNewDiscovery ? 'block' : 'none';
  if (titleEl) titleEl.textContent = isNewDiscovery ? 'New Discovery!' : 'Fish Caught!';

  // Show modal with animation
  if (modal) {
    modal.style.display = 'flex';
    modal.classList.add('show');
  }

  // Adjust camera based on fish size
  previewCamera.position.z = 4 + fish.size;

  // Start animation
  animatePreview();
}

/**
 * Animate the fish preview (rotation)
 */
function animatePreview() {
  if (!isPreviewOpen) return;

  animationId = requestAnimationFrame(animatePreview);

  if (previewFish) {
    // Rotate fish
    previewFish.rotation.y += 0.02;

    // Gentle bob up and down
    previewFish.position.y = Math.sin(Date.now() * 0.002) * 0.1;
  }

  if (previewRenderer && previewScene && previewCamera) {
    previewRenderer.render(previewScene, previewCamera);
  }
}

/**
 * Close fish preview modal
 */
export function closeFishPreview() {
  isPreviewOpen = false;

  // Stop animation
  if (animationId) {
    cancelAnimationFrame(animationId);
    animationId = null;
  }

  // Hide modal
  const modal = document.getElementById('fish-preview-modal');
  if (modal) {
    modal.classList.remove('show');
    modal.style.display = 'none';
  }

  // Call callback if exists
  if (onCloseCallback) {
    onCloseCallback();
    onCloseCallback = null;
  }
}

/**
 * Check if preview is currently open
 */
export function isPreviewVisible() {
  return isPreviewOpen;
}

export default {
  initFishPreview,
  showFishPreview,
  closeFishPreview,
  isPreviewVisible
};
